
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Eye, Heart } from "lucide-react";
import { getArticleBySlugSync } from "@/data/articles/index";
import { ArticleStatsService } from "@/services/ArticleStatsService";
import { useAuth } from "@/context/AuthContext";

interface ArticleStatsProps {
  articleId: string;
  initialReadCount?: number;
  initialLikeCount?: number;
}

const ArticleStats = ({ articleId, initialReadCount, initialLikeCount }: ArticleStatsProps) => {
  const { user } = useAuth();
  
  // Get article data from the corresponding .ts file
  const articleData = getArticleBySlugSync(articleId);

  // Use data from article file if available, otherwise fallback to props or default values
  const defaultReadCount = articleData?.readCount || initialReadCount || 0;
  const defaultLikeCount = articleData?.likeCount || initialLikeCount || 0;
  
  const [readCount, setReadCount] = useState(defaultReadCount);
  const [likeCount, setLikeCount] = useState(defaultLikeCount);
  const [hasLiked, setHasLiked] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadStats = async () => {
      // Fetch article stats
      const stats = await ArticleStatsService.fetchStats(articleId);
      if (isMounted) {
        setReadCount(stats.readCount || defaultReadCount);
        setLikeCount(stats.likeCount || defaultLikeCount);
      }

      // Check user's actual like status from database if authenticated
      if (user?.id) {
        const userLiked = await ArticleStatsService.checkUserLiked(articleId, user.id);
        if (isMounted) {
          setHasLiked(userLiked);
        }
      } else {
        // Fallback to localStorage for non-authenticated users
        const hasLikedArticle = localStorage.getItem(`liked-${articleId}`) === 'true';
        if (isMounted) {
          setHasLiked(hasLikedArticle);
        }
      }

      // Increment read count using secure database function
      await ArticleStatsService.incrementReadCount(articleId, user?.id);
      
      // Fetch updated stats to reflect the increment
      const updatedStats = await ArticleStatsService.fetchStats(articleId);
      if (isMounted) {
        setReadCount(updatedStats.readCount || defaultReadCount);
      }
    };

    loadStats();

    return () => {
      isMounted = false;
    };
  }, [articleId, articleData, defaultReadCount, defaultLikeCount, user?.id]);

  const handleLike = async () => {
    // Require authentication for liking
    if (!user) {
      console.warn("User must be logged in to like articles");
      return;
    }

    // Optimistically update UI
    const wasLiked = hasLiked;
    setHasLiked(!wasLiked);
    setLikeCount(prev => wasLiked ? prev - 1 : prev + 1);

    // Call secure database function
    const newLikeStatus = await ArticleStatsService.toggleLike(articleId, user.id);
    
    // Fetch updated stats to ensure consistency
    const updatedStats = await ArticleStatsService.fetchStats(articleId);
    setLikeCount(updatedStats.likeCount);
    setHasLiked(newLikeStatus);
  };

  return (
    <div className="flex items-center gap-4 text-sm text-muted-foreground">
      <div className="flex items-center gap-1">
        <Eye className="h-4 w-4" /> {readCount} reads
      </div>
      
      <Button 
        variant="ghost" 
        size="sm" 
        className={`flex items-center gap-1 p-1 ${hasLiked ? 'text-red-500' : ''}`} 
        onClick={handleLike}
      >
        <Heart className={`h-4 w-4 ${hasLiked ? 'fill-current' : ''}`} />
        {likeCount}
      </Button>
    </div>
  );
};

export default ArticleStats;
